// src/services/TaskEngine.ts
// Auto-generates tasks from assessments, simulations, applications.
// Writes to tasks table. Emits events on status changes.

import { supabase } from '@/lib/supabase';
import { Task, TaskStatus, TaskPriority, TaskCategory } from '@/types/Journey.types';
import { EventSystem } from './EventSystem';

export class TaskEngine {
  private userId: string;
  private eventSystem: EventSystem;

  constructor(userId: string) {
    this.userId = userId;
    this.eventSystem = new EventSystem(userId);
  }

  async generateTasksFromAssessment(assessmentData: {
    pathway?: string;
    country?: string;
    documents?: { checklist?: Array<{ document: string; priority?: string; status?: string }> };
  }): Promise<Task[]> {
    const tasks: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>[] = [];

    // Document tasks
    if (assessmentData.documents?.checklist) {
      assessmentData.documents.checklist.forEach(doc => {
        if (doc.status !== 'completed') {
          tasks.push({
            userId: this.userId,
            title: `Prepare ${doc.document}`,
            description: `Gather and prepare ${doc.document} for submission`,
            status: 'not_started',
            priority: doc.priority === 'high' ? 'high' : 'medium',
            category: 'documents',
            dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
            estimatedTime: '1-2 hours',
            metadata: { document: doc.document },
          });
        }
      });
    }

    // Application task
    if (assessmentData.pathway) {
      tasks.push({
        userId: this.userId,
        title: `Complete ${assessmentData.pathway} Application`,
        description: `Submit your ${assessmentData.pathway} visa application`,
        status: 'not_started',
        priority: 'high',
        category: 'applications',
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        estimatedTime: '3-4 hours',
        metadata: { pathway: assessmentData.pathway, country: assessmentData.country },
      });
    }

    const saved: Task[] = [];
    for (const task of tasks) {
      const result = await this.saveTask(task);
      if (result) saved.push(result);
    }

    return saved;
  }

  async saveTask(
    task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<Task | null> {
    try {
      const { data, error } = await supabase
        .from('tasks')
        .insert({
          user_id: this.userId,
          title: task.title,
          description: task.description ?? null,
          status: task.status,
          priority: task.priority,
          category: task.category,
          due_date: task.dueDate ?? null,
          estimated_time: task.estimatedTime ?? null,
          metadata: task.metadata ?? {},
        })
        .select()
        .single();

      if (error) {
        console.error('TaskEngine.saveTask error:', error);
        return null;
      }

      return this.transform(data);
    } catch (err) {
      console.error('TaskEngine.saveTask unexpected error:', err);
      return null;
    }
  }

  async updateTaskStatus(taskId: string, status: TaskStatus): Promise<Task | null> {
    try {
      const { data, error } = await supabase
        .from('tasks')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', taskId)
        .eq('user_id', this.userId)
        .select()
        .single();

      if (error) {
        console.error('TaskEngine.updateTaskStatus error:', error);
        return null;
      }

      if (status === 'completed') {
        await this.eventSystem.emit({
          eventType: 'task.completed',
          payload: { taskId, title: data.title },
          source: 'system',
          relatedId: taskId,
        });
      }

      return this.transform(data);
    } catch (err) {
      return null;
    }
  }

  async getTasks(options?: {
    status?: TaskStatus;
    category?: TaskCategory;
    priority?: TaskPriority;
    dueBefore?: string;
  }): Promise<Task[]> {
    try {
      let query = supabase
        .from('tasks')
        .select('*')
        .eq('user_id', this.userId);

      if (options?.status) query = query.eq('status', options.status);
      if (options?.category) query = query.eq('category', options.category);
      if (options?.priority) query = query.eq('priority', options.priority);
      if (options?.dueBefore) query = query.lte('due_date', options.dueBefore);

      const { data, error } = await query.order('due_date', { ascending: true });
      if (error) {
        console.error('TaskEngine.getTasks error:', error);
        return [];
      }

      return (data ?? []).map(this.transform);
    } catch {
      return [];
    }
  }

  async getPendingTasks(): Promise<Task[]> {
    return this.getTasks({ status: 'not_started' });
  }

  async getOverdueTasks(): Promise<Task[]> {
    return this.getTasks({
      status: 'not_started',
      dueBefore: new Date().toISOString(),
    });
  }

  private transform(row: any): Task {
    return {
      id: row.id,
      userId: row.user_id,
      title: row.title,
      description: row.description ?? undefined,
      status: row.status,
      priority: row.priority,
      category: row.category,
      dueDate: row.due_date ?? undefined,
      estimatedTime: row.estimated_time ?? undefined,
      metadata: row.metadata ?? {},
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    };
  }
}
